import * as _ from 'lodash';
import { Component, Input, Output } from '@angular/core';
import { finalize, first, takeUntil } from 'rxjs/operators';

import { ServiceItems, ServiceMembers, ServiceFollowers, ServiceSecurity, ServiceToaster, ServiceErrors, ServiceTextAnalyser } from '@services';
import { Member } from '@models';
import { LinkedItemsComponent } from '../linked-items.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'followed-members',
    templateUrl: './followed-members.component.html',
    styleUrls: ['./followed-members.component.scss']
})
export class FollowedMembersComponent extends LinkedItemsComponent {

    private _member: Member;
    @Input()
    set member(value: Member) {
        this._member = value;
        this.handleEditionMode(this._editionMode);
    }
    get member(): Member {
        return this._member;
    }

    constructor(
        private serviceItems: ServiceItems,
        private serviceErrors: ServiceErrors,
        private serviceToaster: ServiceToaster,
        private serviceMembers: ServiceMembers,
        private serviceFollowers: ServiceFollowers,
        private serviceSecurity: ServiceSecurity, 
        protected serviceTextAnalyser: ServiceTextAnalyser
    ) {
        super(serviceMembers, serviceTextAnalyser);
        this.field = 'followedMembers';
    }

    protected handleOrder(orderDirection: 'asc' | 'desc') {
        if (this._member instanceof Member) {
            this._member.followedMembers = this.serviceMembers.sortArray(this._member.followedMembers, orderDirection);
        }
    }

    protected handleEditionMode(value: boolean) {
        if (this._member instanceof Member) {
            this.isCreation = _.isUndefined(this._member.id);
            this.canEdit = this.serviceMembers.userCanModifyFollowedMembers(this._member);
            if (value === true && this.canEdit === true) {
                this.loadAllPotentialLinkedItems<Member>(this._member.followedMembers, this._member);
            }
        }
    }

    addLinkedItem(followedMember: Member) {
        followedMember.isLoading = true;
        this.ctrl.reset();
        this.ctrl.disable();
        this.actionInProgress = true;

        let followedMembers = this._member.followedMembers;
        followedMembers.push(followedMember);
        followedMembers = this.serviceMembers.sortArray(followedMembers, 'asc');
        this._member.followedMembers = followedMembers;

        if (!this.isCreation) {
            this.serviceFollowers.memberAStartsToFollowMemberB(this._member, followedMember).pipe(
                finalize(() => {
                    this.ctrl.enable();
                    this.actionInProgress = false;
                    this.newLinkInput.nativeElement.focus();
                }),
                first(),
                takeUntil(this._destroyed$)
            ).subscribe(
                (result: any) => {
                    followedMember.isLoading = false;
                    _.remove(this.allPotentialLinkedItems, function (o: Member) { return o.id === followedMember.id; });

                    this._member.followedMembers = followedMembers;
                    if (this._member.witness) {
                        this._member.witness.followedMembers = followedMembers;
                    }
                    if (this.serviceSecurity.isCurrentUser(followedMember)) {
                        this._member.isUserFollowing = true;
                        if (this._member.witness) {
                            this._member.witness.isUserFollowing = true;
                        }
                    }
                    this.serviceItems.item = this._member;

                    const params = { member: _.get(result, 'itemFrom.title'), title: _.get(result, 'itemTo.title') };
                    this.serviceToaster.success('ITEM.FOLLOWERS.MEMBER.ADD', params);
                },
                (error: HttpErrorResponse) => {
                    _.remove(this._member.followedMembers, function (o: Member) { return o.id === followedMember.id; });
                    this.serviceErrors.handleError('ITEM.FOLLOWERS.MEMBER.ADD', error);
                });
        } else {
            followedMember.isLoading = false;
            _.remove(this.allPotentialLinkedItems, function (o: Member) { return o.id === followedMember.id; });
            this.ctrl.enable();
            this.actionInProgress = false;
            this.newLinkInput.nativeElement.focus();
        }
    }

    removeLinkedItem(followedMember: Member) {
        followedMember.isLoading = true;
        this.ctrl.disable();
        this.actionInProgress = true;

        if (!this.isCreation) {
            this.serviceFollowers.memberAStopToFollowMemberB(this._member, followedMember).pipe(
                finalize(() => {
                    followedMember.isLoading = false;
                    this.ctrl.enable();
                    this.actionInProgress = false;
                }),
                first(),
                takeUntil(this._destroyed$)
            ).subscribe(
                (result: any) => {
                    this.allPotentialLinkedItems.push(followedMember);

                    const followedMembers = this._member.followedMembers;
                    _.remove(this._member.followedMembers, function (o: Member) { return o.id === followedMember.id; });

                    this._member.followedMembers = followedMembers;
                    if (this._member.witness) {
                        this._member.witness.followedMembers = followedMembers;
                    }
                    if (this.serviceSecurity.isCurrentUser(followedMember)) {
                        this._member.isUserFollowing = false;
                        if (this._member.witness) {
                            this._member.witness.isUserFollowing = false;
                        }
                    }
                    this.serviceItems.item = this._member;

                    const params = { member: _.get(result, 'itemFrom.title'), title: _.get(result, 'itemTo.title') };
                    this.serviceToaster.success('ITEM.FOLLOWERS.MEMBER.REMOVE', params);
                },
                (error: HttpErrorResponse) => {
                    let followedMembers = this._member.followedMembers;
                    followedMembers.push(followedMember);
                    followedMembers = this.serviceMembers.sortArray(followedMembers, 'asc');

                    this._member.followedMembers = followedMembers;
                    if (this._member.witness) {
                        this._member.witness.followedMembers = followedMembers;
                    }
                    if (this.serviceSecurity.isCurrentUser(followedMember)) {
                        this._member.isUserCreator = true;
                        if (this._member.witness) {
                            this._member.witness.isUserCreator = true;
                        }
                    }
                    this.serviceItems.item = this._member;

                    this.serviceErrors.handleError('ITEM.FOLLOWERS.MEMBER.REMOVE', error);
                }
            );
        } else {
            this.allPotentialLinkedItems.push(followedMember);
            _.remove(this._member.followedMembers, function (o: Member) { return o.id === followedMember.id; });
            followedMember.isLoading = false;
            this.ctrl.enable();
            this.actionInProgress = false;
        }
    }
}
